//Libs
import { useState } from "react";
import TrashIcon from "@heroicons/react/24/solid/TrashIcon";
import CheckIcon from "@heroicons/react/24/solid/CheckIcon";

//Local
import BaseInputTextArea from "../BaseInputTextArea";
import BaseInputNumber from "../BaseInputNumber";
import currencyFormatter from "../../currency";
import {
  getInvoiceLineItemTotal,
  getTemporaryItemTotal,
} from "../../assets/js/estimateFunctions";
import { ItemMembershipData } from "../../models/item-membership-data";
import { StiltLineItemFormDataExtended } from "../../models/stilt-payment";
import { DbRead } from "../../database";
import { ExistingCustomer } from "../../models/customer";
import AddMembershipToItem from "../estimates/AddMembershipToItem";
import { StyledTooltip } from "../StyledTooltip";
import { WARNING_MEMBERSHIP_ALREADY_CREATED_FROM_LINE_ITEM } from "../../strings";

interface Props {
  //DATA
  lineItem: StiltLineItemFormDataExtended;
  currency: string;
  siteKey: string;
  totalTaxRate: number;
  globalDiscount: number;
  customer: ExistingCustomer;
  hasTask: boolean;
  initialMembershipData: ItemMembershipData | null;
  isMembershipInvoice: boolean;
  //FUNCTIONS
  handleSaveItem: (updatedItem: StiltLineItemFormDataExtended) => void;
  handleDeleteItem: () => void;
}

export default function EditInvoiceItemLine({
  handleSaveItem,
  handleDeleteItem,
  currency,
  lineItem,
  siteKey,
  totalTaxRate,
  globalDiscount,
  customer,
  hasTask,
  initialMembershipData,
  isMembershipInvoice,
}: Props) {
  const [displayError, setDisplayError] = useState(false);

  const [description, setDescription] = useState(lineItem.description ?? "");
  const [unitPrice, setUnitPrice] = useState<number | "">(lineItem.unitPrice);
  const [discount, setDiscount] = useState<number | "">(
    lineItem.discount ?? "",
  );
  const [quantity, setQuantity] = useState<number | "">(lineItem.quantity);

  const [itemMembershipData, setItemMembershipData] =
    useState<ItemMembershipData | null>(initialMembershipData);

  const onSubmit = async () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { membershipData, ...rest } = lineItem;
    const updatedItem: StiltLineItemFormDataExtended = {
      ...rest,
      toBeEdited: false,
      description: description,
      title: lineItem.title,
      unitPrice: unitPrice === "" ? 0 : unitPrice,
      quantity: quantity === "" ? 0 : quantity,
      discount: discount === "" ? 0 : discount,
    };

    if (itemMembershipData) updatedItem.membershipData = itemMembershipData;
    // prevent duplicate membership creation
    if (lineItem.membershipID) updatedItem.membershipData = undefined;

    if (lineItem.priceBookItemID) {
      const pbItem = await DbRead.priceBookItems.get(
        siteKey,
        lineItem.priceBookItemID,
      );
      const { taxAmountItem, subtotalItemWithLineDiscount } =
        getInvoiceLineItemTotal({
          item: updatedItem,
          currency: currency,
          totalTaxRate: totalTaxRate,
          globalDiscount: globalDiscount,
          customer: customer,
          discountable: pbItem?.discountable ?? true,
          taxable: pbItem?.taxable ?? true,
        });
      updatedItem.subTotal = subtotalItemWithLineDiscount;
      updatedItem.taxAmount = taxAmountItem;
    }

    handleSaveItem(updatedItem);
  };

  return (
    <div className="flex flex-col items-center sm:grid sm:grid-cols-[5fr,1fr] lg:grid-cols-[8fr,1fr]">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-[2fr,1fr,1fr,1fr] ">
          <div
            className={`relative col-span-2 block w-full min-w-0 rounded border border-primary p-4 text-gray-700 focus:border-primaryLight focus:outline-none focus:ring focus:ring-primaryLight sm:text-sm lg:col-span-1`}
          >
            <div className="truncate">{lineItem.title}</div>
            <label className="absolute -top-3 left-3 bg-white px-2">
              Title
              <span className="text-lg font-medium text-redFail">*</span>
            </label>
          </div>

          <BaseInputNumber
            text="Quantity"
            inputName="quantity"
            admin={true}
            required={true}
            value={quantity}
            onChange={(event) => {
              const numberValue = event.target.valueAsNumber;
              if (displayError === true && numberValue > 0) {
                setDisplayError(false);
              }
              setQuantity(isNaN(numberValue) ? "" : numberValue);
            }}
          />

          <BaseInputNumber
            text="Unit Price"
            inputName="unitPrice"
            admin={true}
            required={true}
            value={unitPrice}
            onChange={(event) => {
              const numberValue = event.target.valueAsNumber;
              if (displayError === true && numberValue > 0) {
                setDisplayError(false);
              }
              setUnitPrice(isNaN(numberValue) ? "" : numberValue);
            }}
          />

          {lineItem.discountable && (
            <BaseInputNumber
              text="Discount (%)"
              inputName="discount"
              admin={true}
              required={false}
              disabled={!lineItem.discountable}
              value={discount ?? 0}
              onChange={(event) => {
                const numberValue = event.target.valueAsNumber;
                if (displayError === true && numberValue > 0) {
                  setDisplayError(false);
                }
                setDiscount(isNaN(numberValue) ? "" : numberValue);
              }}
            />
          )}
        </div>

        <BaseInputTextArea
          text="Description"
          inputName="description"
          admin={true}
          required={false}
          rows={3}
          value={description ?? ""}
          onChange={(event) => setDescription(event.target.value)}
        />

        <AddMembershipToItem
          type="invoice"
          siteKey={siteKey}
          isMembershipInvoice={isMembershipInvoice}
          hasTask={hasTask}
          priceBookItemID={lineItem.priceBookItemID ?? ""}
          createdMembershipID={lineItem.membershipID}
          membershipData={itemMembershipData}
          setMembershipData={setItemMembershipData}
        />
      </div>
      <div className="mt-4 flex flex-row items-center justify-between gap-4 sm:mt-0 sm:flex-col sm:items-end">
        <div className="text-lg font-medium">
          {currencyFormatter(
            getTemporaryItemTotal(
              quantity,
              unitPrice,
              lineItem.discountable ?? true,
              discount,
            ),
            currency,
          )}
        </div>
        <StyledTooltip
          title={`${lineItem.membershipID ? WARNING_MEMBERSHIP_ALREADY_CREATED_FROM_LINE_ITEM : ""}`}
        >
          <button
            className="mx-1 rounded-full hover:bg-red-50"
            onClick={() => handleDeleteItem()}
          >
            <TrashIcon
              aria-label="delete button"
              className="block h-10 w-10 cursor-pointer p-2 text-red-700"
            />
          </button>
        </StyledTooltip>
        <button
          className="mx-1 rounded-full hover:bg-green-50"
          onClick={onSubmit}
        >
          <CheckIcon
            aria-label="confirm button"
            className="block h-10 w-10 cursor-pointer p-2 text-green-700"
          />
        </button>
      </div>
    </div>
  );
}
